import { ContextApp } from '../contexts/ContextApp';
import { useContext, useState } from 'react';
import { logoutFirebase } from '../configs/firebase/firebase-config';
import { EnumsValues } from '../enums/EnumsValues';
import { Query } from './graphql/query';
import { useCustomLazyQuery } from '../hooks/apollo/ApolloCustomHooks';
import { IUser } from '../interfaces';
import { useCustomMessage } from '../hooks/useCustomMessage';

export const UserService = () => {
  const { messageError } = useCustomMessage();
  const {
    setUser,
    setRoles,
    setFunctions,
    setFeatures,
    setSessionExpired,
    user,
    setUserDataFirebase,
    setPictureProfileUrl,
    setTenantsAssociatedToUser,
    t,
  } = useContext(ContextApp);

  const [authenticateData, setAuthenticateData] = useState<IUser>();

  const [fetchAuthenticate] = useCustomLazyQuery<{
    authenticate: IUser & { __typename: string };
  }>(Query.authenticate, { fetchPolicy: 'network-only' });

  const [fetchLogout] = useCustomLazyQuery<{
    logout: unknown;
  }>(Query.logout, { fetchPolicy: 'no-cache' });

  const authenticate = async (lastTenantId?: number) => {
    const { data, error } = await fetchAuthenticate({
      variables: { tenant_id: lastTenantId },
    });
    if (error) throw error;
    if (data?.authenticate?.__typename !== 'AuthOutput') {
      throw new Error(t('error.backend.generalErrorMessage'));
    }
    const authenticatedUser = data.authenticate;
    setAuthenticateData(structuredClone(authenticatedUser));
    return authenticatedUser;
  };

  const logout = async () => {
    // Clearing the user unmounts the caller. Keep this request alive so the
    // backend can clear the refresh cookie after the local session is gone.
    const logoutRequest = fetchLogout().retain();
    // The logout endpoint clears the refresh cookie; access-token revocation is not supported by the backend.
    localStorage.removeItem(EnumsValues.LocalStorageKeys.Token);
    localStorage.removeItem(EnumsValues.LocalStorageKeys.LastTenantId);
    localStorage.removeItem(EnumsValues.LocalStorageKeys.TwoFactorValidated);
    localStorage.removeItem(EnumsValues.LocalStorageKeys.UserId);
    localStorage.removeItem(EnumsValues.LocalStorageKeys.googleImage);
    if (user?.uid) {
      logoutFirebase();
    }
    setAuthenticateData(undefined);
    setUser(undefined);
    setRoles(() => []);
    setFunctions(() => []);
    setFeatures(() => []);
    setSessionExpired(false);
    setUserDataFirebase(undefined);
    setPictureProfileUrl('');
    setTenantsAssociatedToUser(() => []);
    try {
      const { error } = await logoutRequest;
      if (error) throw error;
    } catch (error) {
      messageError({ context: 'UserService.logout', error });
    }
  };

  const expireSession = () => {
    try {
      localStorage.removeItem(EnumsValues.LocalStorageKeys.Token);
      setRoles(() => []);
      setFunctions(() => []);
      setFeatures(() => []);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
  return {
    authenticateData,
    authenticate,
    logout,
    expireSession,
  };
};
